import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Card, Row, Col, Table, Typography, Space, Input, message, Select, Divider } from 'antd';
import InvoicePDF from '../../components/InvoicePDF';
import { ActionButton } from '../../components/DesignSystem';
import { evaluateBillingContext } from '../../utils/orderInvoiceRules';
import { sendInvoiceEmail } from '../../utils/invoiceEmail';
import { hasCompleteBankDetails, parseBankDetails, normalizeBankDetails } from '../../utils/bankDetails';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';

const { Title, Text } = Typography;


const InvoiceView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<any>(null);
  const [businessSettings, setBusinessSettings] = useState<any>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [customer, setCustomer] = useState<any>(null); // full customer record for phone/email
  const [payments, setPayments] = useState<any[]>([]);
  const [emailAddress, setEmailAddress] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchPayments = () =>
    apiFetch(`${API_URL}/invoice-payments/invoice/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : []).then(setPayments).catch(() => {});

  useEffect(() => {
    if (!token) {
      // Not authenticated - redirect to login
      navigate('/login');
      return;
    }

    // Fetch invoice, business settings, and customers
    Promise.all([
      apiFetch(`${API_URL}/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => res.ok ? res.json() : Promise.reject(`Status ${res.status}`)),
      
      apiFetch(`${API_URL}/business`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => res.ok ? res.json() : null),
      
      apiFetch(`${API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => res.ok ? res.json() : []),

      apiFetch(`${API_URL}/invoice-payments/invoice/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => res.ok ? res.json() : [])
    ])
      .then(([invoiceData, businessData, customersData, paymentsData]) => {
        const normalized = {
          ...invoiceData,
          status: invoiceData.status === 'Partially Paid' ? 'Partial'
            : invoiceData.status === 'Pending' ? 'Unpaid' : invoiceData.status,
        };
        setInvoice(normalized);
        setBusinessSettings(businessData);
        setCustomers(Array.isArray(customersData) ? customersData : []);
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
        const initEmail = invoiceData.customer_email || invoiceData.customerEmail || '';
        setEmailAddress(initEmail);
        // Fetch full customer to get email/phone if they aren't on the invoice
        const custId = invoiceData.customer_id || invoiceData.customerId;
        if (custId) {
          apiFetch(`${API_URL}/customers/${custId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(c => { if (c) { setCustomer(c); if (!initEmail && c.email) setEmailAddress(c.email); } })
            .catch(() => {});
        }
      })
      .catch(err => {
        console.error('❌ Failed to fetch data:', err);
        setInvoice(null);
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  if (loading) return <p style={s.msg}>⏳ Loading invoice...</p>;
  if (!invoice) return <p style={s.msg}>❌ Invoice not found</p>;

  // DB returns snake_case — support both with fallback
  const g = (snake: string, camel?: string) => invoice[snake] ?? (camel ? invoice[camel] : undefined);
  const invoiceNumber  = g('invoice_number',  'invoiceNumber')  || invoice.id;
  const invoiceDate    = g('invoice_date',    'invoiceDate');
  const dueDate        = g('due_date',        'dueDate')        || g('payment_due_date', 'paymentDueDate');
  const clientName     = g('client_name',     'clientName');
  const formatCustomerAddress = (c: any) =>
    c ? [c.address, c.city, c.postcode, c.country].filter(Boolean).join(', ') : '';

  const custAddress    = g('customer_address','customerAddress') || formatCustomerAddress(customer) || '';
  // Prefer full customer record (fetched separately) — fallback to invoice fields
  const custEmail = customer?.email || g('customer_email', 'customerEmail') || '';
  const custPhone = customer?.phone || g('customer_phone', 'customerPhone') || '';

  // Business logo
  const logoUrl = businessSettings?.logo
    ? (businessSettings.logo.startsWith('http') ? businessSettings.logo : `${API_URL.replace('/api', '')}${businessSettings.logo}`)
    : null;
  const description    = g('description')     || g('notes');
  const appliedCredit  = Number(g('applied_credit', 'appliedCredit') || 0);
  const paidAmount     = Number(g('paid_amount', 'paidAmount') || 0);
  const fmt = (v: any) => v ? new Date(v).toLocaleDateString('en-GB') : 'N/A';
  const invoiceTotal = Number(invoice.total) || Number(invoice.amount) || 0;
  const balanceDue = Math.max(0, invoiceTotal - paidAmount);
  const bank = normalizeBankDetails(parseBankDetails(businessSettings || {}), businessSettings?.name);
  const showBankDetails = !['Paid', 'Cancelled', 'Void', 'Draft'].includes(String(invoice.status || ''));

  const lineItems = Array.isArray(invoice.items) && invoice.items.length > 0
    ? invoice.items
    : [{
        description: invoice.description || 'Service',
        quantity: Number(invoice.quantity) || 1,
        unitPrice: Number(invoice.unitPrice || invoice.unit_price) || 0,
        discount: Number(invoice.discount || invoice.discount_rate) || 0,
        vatRate: Number(invoice.vatRate || invoice.vat_rate) || 0
      }];

  const itemColumns = [
    {
      title: 'Description', dataIndex: 'description', key: 'description',
      render: (v: any, record: any) => (
        <div>
          <div>{v}</div>
          {(record.saleType === 'box' || record.sale_type === 'box') && record.box_quantity && (
            <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600, marginTop: 2 }}>📦 Box of {record.box_quantity}</div>
          )}
        </div>
      )
    },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 90 },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      render: (v: any) => `£${Number(v || 0).toFixed(2)}`
    },
    {
      title: 'Disc %',
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (v: any) => `${Number(v || 0)}%`
    },
    {
      title: 'VAT %',
      dataIndex: 'vatRate',
      key: 'vatRate',
      width: 100,
      render: (v: any) => `${Number(v || 0)}%`
    },
    {
      title: 'Line Total',
      key: 'lineTotal',
      width: 130,
      render: (_: any, record: any) => {
        const qty = Number(record.quantity) || 0;
        const unit = Number(record.unitPrice || record.unit_price) || 0;
        const disc = Number(record.discount || record.discount_rate) || 0;
        const net = qty * unit * (1 - disc / 100);
        return `£${net.toFixed(2)}`;
      }
    }
  ];

  const paymentColumns = [
    {
      title: 'Date & Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_: any, record: any) =>
        new Date(record.created_at || record.payment_date).toLocaleString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        })
    },
    { title: 'Method', dataIndex: 'payment_method', key: 'payment_method' },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (v: any) => v || '-'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (v: any) => <Text strong>{`£${Number(v || 0).toFixed(2)}`}</Text>
    }
  ];

  const onSendEmail = async () => {
    if (!token || !id) return;
    if (!emailAddress) { message.warning('Please enter or select a customer email address.'); return; }
    try {
      setSending(true);
      await sendInvoiceEmail(id, token, emailAddress);
      message.success(`Invoice sent to ${emailAddress}`);
    } catch (error: any) {
      message.error(error?.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const onRecordPayment = async (_values: any) => {}; // unused placeholder
  const STATUS_COLORS: Record<string, string> = {
    Draft: '#6b7280', Sent: '#3b82f6', Partial: '#f59e0b', 'Partially Paid': '#f59e0b',
    Paid: '#10b981', Overdue: '#ef4444', Cancelled: '#9ca3af', Unpaid: '#ef4444', Pending: '#3b82f6',
  };
  const sc = STATUS_COLORS[invoice.status] || '#667eea';
  const billing = evaluateBillingContext(invoice);
  const isDraft = billing.canEditInvoiceLines;
  const sourceOrderId = invoice.source_order_id || invoice.sourceOrderId;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <Title level={2} style={{ margin: 0 }}>🧾 Invoice #{invoiceNumber}</Title>
        <Space wrap>
          <ActionButton variant="view" label="Print" onClick={() => window.print()} />
          {isDraft && (
            <ActionButton variant="edit" label="Edit" onClick={() => navigate(`/invoices/edit/${id}`)} />
          )}
          {!isDraft && (
            <button
              type="button"
              title="Only Draft invoices can be edited"
              onClick={() => navigate(`/invoices/edit/${id}`)}
              style={{ padding: '8px 16px', background: '#fef3c7', color: '#92400e', border: '1.5px solid #fbbf24', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🔒 Locked
            </button>
          )}
          <ActionButton variant="cancel" label="Back" onClick={() => navigate('/invoices/list')} />
        </Space>
      </div>

      {/* Status lifecycle banner */}
      {!isDraft && (
        <div style={{ marginBottom: 16, padding: '14px 20px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <strong style={{ color: '#92400e' }}>🔒 This invoice is locked for editing.</strong>
            <div style={{ fontSize: 13, color: '#78350f', marginTop: 4 }}>{billing.invoiceEditHint}</div>
          </div>
          <span style={{ padding: '5px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: sc + '22', color: sc, border: `1.5px solid ${sc}` }}>
            {invoice.status}
          </span>
        </div>
      )}

      <Card>
        {/* Business header with logo */}
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={4}>
            {logoUrl
              ? <img src={logoUrl} alt="logo" style={{ maxHeight: 72, maxWidth: 120, objectFit: 'contain' }} />
              : <div style={{ width: 60, height: 60, borderRadius: 10, background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 26, fontWeight: 700 }}>
                  {(businessSettings?.name || 'S').charAt(0)}
                </div>
            }
          </Col>
          <Col xs={24} md={12}>
            <Title level={4} style={{ margin: '0 0 2px' }}>{businessSettings?.name || 'Your Business'}</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {[businessSettings?.address, businessSettings?.city, businessSettings?.postcode, businessSettings?.country].filter(Boolean).join(', ')}
            </Text>
            {businessSettings?.email && <><br /><Text style={{ fontSize: 12 }}>{businessSettings.email}</Text></>}
            {businessSettings?.phone && <><br /><Text style={{ fontSize: 12 }}>{businessSettings.phone}</Text></>}
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ letterSpacing: 2, fontSize: 11 }}>INVOICE</Text>
            <Title level={3} style={{ margin: '0 0 6px' }}>#{invoiceNumber}</Title>
            <span style={{ background: sc, color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{invoice.status || 'Unpaid'}</span>
            {sourceOrderId && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => navigate(`/orders/view/${sourceOrderId}`)}
                  style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}
                >
                  📦 View Source Order #{sourceOrderId}
                </button>
              </div>
            )}
          </Col>
        </Row>
        <Divider />

        <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
          <Col xs={24} md={12}>
            <Card size="small" title="Client Information" style={{ height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                {customer?.image && customer.image.startsWith('http') ? (
                  <img
                    src={customer.image}
                    alt={clientName || 'Customer'}
                    style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 20, flexShrink: 0 }}>
                    {(clientName || 'C').charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <Text strong style={{ fontSize: 15 }}>{clientName || '—'}</Text>
                  {(customer?.companyName || customer?.companyname) && (
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{customer?.companyName || customer?.companyname}</Text></div>
                  )}
                </div>
              </div>
              <p style={{ margin: '5px 0' }}><strong>Email:</strong> {custEmail ? <a href={`mailto:${custEmail}`}>{custEmail}</a> : <Text type="secondary">—</Text>}</p>
              <p style={{ margin: '5px 0' }}><strong>Phone:</strong> {custPhone ? <a href={`tel:${custPhone}`}>{custPhone}</a> : <Text type="secondary">—</Text>}</p>
              <p style={{ margin: '5px 0' }}><strong>Address:</strong> {custAddress || <Text type="secondary">—</Text>}</p>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title="Invoice Details">
              <p><strong>Invoice #:</strong> {invoiceNumber}</p>
              <p><strong>Invoice Date:</strong> {fmt(invoiceDate)}</p>
              <p><strong>Due Date:</strong> {fmt(dueDate)}</p>
              <p><strong>Status:</strong> {invoice.status || 'Unpaid'}</p>
              {description && <p><strong>Description:</strong> {description}</p>}
            </Card>
          </Col>
        </Row>

        <Card title="Item Details" size="small" style={{ marginTop: 16 }}>
          <Table
            rowKey={(r: any, idx: number) => String(r.id || idx)}
            columns={itemColumns}
            dataSource={lineItems}
            pagination={false}
            size="small"
            scroll={{ x: 760 }}
          />
        </Card>

        <Card size="small" style={{ marginTop: 16 }}>
          <div style={s.totalRow}><span>Subtotal:</span><strong>£{(Number(invoice.subtotal) || 0).toFixed(2)}</strong></div>
          <div style={s.totalRow}><span>VAT:</span><strong>£{(Number(invoice.total_vat) || Number(invoice.vatAmount) || Number(invoice.totalVat) || 0).toFixed(2)}</strong></div>
          {appliedCredit > 0 && <div style={s.totalRow}><span>Credit Applied:</span><strong>-£{appliedCredit.toFixed(2)}</strong></div>}
          <div style={{ ...s.totalRow, ...s.totalFinal }}><span>Total:</span><span>£{(Number(invoice.total) || Number(invoice.amount) || 0).toFixed(2)}</span></div>
          {paidAmount > 0 && (() => {
            const sumPmts = payments.filter((p: any) => !p.is_void).reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
            const depositPortion = Math.max(0, paidAmount - sumPmts);
            return (
              <>
                {depositPortion > 0.005 && (
                  <div style={{ ...s.totalRow, color: '#10b981' }}><span>💼 Order Deposit:</span><strong>-£{depositPortion.toFixed(2)}</strong></div>
                )}
                {sumPmts > 0.005 && (
                  <div style={{ ...s.totalRow, color: '#10b981' }}><span>💳 Payments Made:</span><strong>-£{sumPmts.toFixed(2)}</strong></div>
                )}
                <div style={{ ...s.totalRow, ...s.totalFinal, color: '#f59e0b' }}>
                  <span>⏳ Remaining Balance:</span>
                  <span>£{Math.max(0, (Number(invoice.total) || Number(invoice.amount) || 0) - paidAmount).toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </Card>

        {showBankDetails && (
          <Card title="Payment instructions" size="small" style={{ marginTop: 16, background: '#eff6ff', borderColor: '#bfdbfe' }}>
            {hasCompleteBankDetails(bank) ? (
              <>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#1e40af' }}>
                  Please transfer <strong>£{balanceDue > 0 ? balanceDue.toFixed(2) : invoiceTotal.toFixed(2)}</strong> by bank transfer.
                </p>
                <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Account name:</strong> {bank.bank_account_name || bank.bank_name}</p>
                <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Sort code:</strong> {bank.bank_sort_code}</p>
                <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Account number:</strong> {bank.bank_account_number}</p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#92400e' }}><strong>Payment reference:</strong> {invoiceNumber}</p>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>These details are included when you send the invoice by email.</p>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                Bank details not configured — add them in Settings → Bank Transfer Details so they appear here and in customer emails.
              </p>
            )}
          </Card>
        )}

        <Card title="Payment Activity" size="small" style={{ marginTop: 16 }}>
          <Table
            rowKey={(_: any, idx: number) => String(idx)}
            columns={paymentColumns}
            dataSource={payments}
            pagination={false}
            size="small"
            locale={{ emptyText: 'No payment records found yet.' }}
            scroll={{ x: 560 }}
          />
        </Card>

        <Card title="Send Invoice" size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              placeholder="Search customer"
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={customers.map((c: any) => ({
                value: c._id || c.id,
                label: c.name,
                email: c.email
              }))}
              onChange={(value) => {
                const selected = customers.find((c: any) => String(c._id || c.id) === String(value));
                setEmailAddress(selected?.email || '');
              }}
            />
            <Input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="Customer email address"
            />
            <Space wrap>
              <ActionButton variant="save" label={sending ? 'Sending...' : '💾 Send Email'} onClick={onSendEmail} />
              <PDFDownloadLink
                document={<InvoicePDF invoice={{ ...invoice, invoiceNumber, clientName, customerEmail: custEmail, customerPhone: custPhone, customerAddress: custAddress }} businessSettings={businessSettings} />}
                fileName={`invoice-${invoiceNumber}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                {({ loading: pdfLoading }) => (
                  <ActionButton
                    variant="add"
                    label={pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
                    onClick={() => {}}
                  />
                )}
              </PDFDownloadLink>
            </Space>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

const s: any = {
  container: {maxWidth:1000,margin:'24px auto',padding:'0 12px',fontFamily:'Arial,sans-serif'},
  msg: {padding:40,textAlign:'center',fontSize:18},
  header: {display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:12},
  totalRow: {display:'flex',justifyContent:'space-between',marginBottom:8,fontSize:14},
  totalFinal: {fontSize:18,fontWeight:700,marginTop:10},
  notes: {background:'#f8f9fa',padding:15,borderRadius:6,whiteSpace:'pre-wrap'}
};

export default InvoiceView;
